import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Brain, FileText, BarChart3, Ruler } from "lucide-react";

const painPoints = [
  {
    icon: Brain,
    title: '"No recuerdo qué dijo exactamente"',
    description:
      "Pierdes detalles importantes mientras intentas tomar notas. Tu mente está dividida entre escuchar y escribir, y siempre algo se escapa.",
  },
  {
    icon: FileText,
    title: '"Paso más tiempo documentando que atendiendo"',
    description:
      "15-20 minutos extra por consulta haciendo notas y resúmenes. Multiplica eso por cada cliente y cada día. Tiempo que no facturas.",
  },
  {
    icon: BarChart3,
    title: '"Mi cliente no ve su progreso"',
    description:
      "Datos dispersos entre cuadernos, Excel y apps. Tu cliente necesita ver su evolución para mantener la adherencia. Sin visualización, no hay motivación.",
  },
  {
    icon: Ruler,
    title: '"¿La medida quedó bien o la repito?"',
    description:
      "Las mediciones corporales requieren precisión milimétrica. Sin validación automática, siempre queda la duda de si el pliegue quedó bien medido.",
  },
];

export const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="problema"
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#E05D3A] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            El Problema
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6"
          >
            ¿Te suena familiar?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto"
          >
            Cada consulta nutricional tiene los mismos problemas silenciosos que te cuestan 
            tiempo, energía y profesionalismo.
          </motion.p>
        </div>

        {/* Pain Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {painPoints.map((pain, index) => (
            <motion.div
              key={pain.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className="group relative p-8 rounded-2xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] hover:border-[#E05D3A]/30 transition-all duration-300"
              data-testid={`problem-card-${index}`}
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#E05D3A]/0 to-[#E05D3A]/0 group-hover:from-[#E05D3A]/5 group-hover:to-transparent transition-all duration-300" />
              
              <div className="relative z-10">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 rounded-xl bg-[#E05D3A]/10 text-[#E05D3A]">
                    <pain.icon className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="font-outfit text-xl font-medium text-white mb-3">
                  {pain.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {pain.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
